// ✅ src/pages/VehicleV2/VehicleStatusBranchwiseReport_Print.jsx
// React PRINT PAGE for: Vehicle Status Branchwise Report (Summary grid like gv_listitems1)
//
// Assumptions (matches your ASPX print logic):
// - Backend exposes a print endpoint that returns rows (DataTable-like) for the branchwise summary.
// - Example endpoint (change if yours differs):
//     GET https://nissi-india.com/api/vehicleStatusReport/branchwise-print
//     params: vehMode, vehicleType, month, year
//
// Supports:
// ✅ vehMode / vehicleType / month / year from navigate state OR querystring
// ✅ Dynamic columns (whatever keys backend returns: branches/sites)
// ✅ A4 landscape print styling
// ✅ CSV export (optional)

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/vehicleStatusReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();
const isEmpty = (v) => !toStr(v);

const safeNum = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const fmtInt = (v) => {
  const n = safeNum(v);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
};

const downloadCsv = (rows, filename = "vehicle-status-branchwise.csv") => {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const val = r?.[h];
          const s = val === null || val === undefined ? "" : String(val);
          // escape commas/quotes/newlines
          const esc = s.replace(/"/g, '""');
          return /[",\n]/.test(esc) ? `"${esc}"` : esc;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

const COMMON_LABELS = {
  VehicleTypes: "Vehicle Main Type",
  VehicleType: "Vehicle Type",
  total: "Total",
  Total: "Total",
};

function buildColumns(rows) {
  if (!rows?.length) return [];

  // prefer these first
  const priority = ["VehicleTypes", "VehicleType", "total", "Total"];

  const keys = Object.keys(rows[0]);

  // keys that look like "branch/site" columns:
  // exclude known base columns and any internal id-like columns
  const excluded = new Set(["id", "sno", "SNo", ...priority]);

  const dynamic = keys
    .filter((k) => !excluded.has(k))
    .filter((k) => !/^fld_/i.test(k)) // just in case
    .filter((k) => k !== "__proto__");

  // Compose final columns: priority ones that exist + dynamic
  const cols = [
    ...priority.filter((p) => keys.includes(p)),
    ...dynamic,
  ];

  // ensure unique
  return Array.from(new Set(cols));
}

export default function VehicleStatusBranchwiseReportPrint() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ✅ Inputs from state OR query
  const vehMode =
    toStr(location?.state?.vehMode) ||
    toStr(searchParams.get("vehMode")) ||
    "O"; // O=Own, H=Hire

  const vehicleType =
    toStr(location?.state?.vehicleType) ||
    toStr(searchParams.get("vehicleType")) ||
    "Select";

  const month =
    toStr(location?.state?.month) ||
    toStr(searchParams.get("month")) ||
    "Select";

  const year =
    toStr(location?.state?.year) ||
    toStr(searchParams.get("year")) ||
    "Select";

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        // ✅ Call your backend print API (change route if different)
        const res = await axios.get(`${API_BASE}/print/branchwise`, {
          params: {
            vehMode,
            vehicleType,
            month,
            year,
          },
        });

        const dataRows = res?.data?.rows || res?.data?.data || res?.data || [];
        if (!Array.isArray(dataRows)) throw new Error("Invalid response: rows not found");

        if (alive) setRows(dataRows);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [vehMode, vehicleType, month, year]);

  const columns = useMemo(() => buildColumns(rows), [rows]);

  // Add S.No for print table
  const displayRows = useMemo(() => {
    return (rows || []).map((r, idx) => ({ __sno: idx + 1, ...r }));
  }, [rows]);

  const title = "Vehicle Status - Branchwise Report";

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      {/* Print Styles */}
      <style>{`
        @page { size: A4 landscape; margin: 0.3in; }
        @media print {
          body { margin: 0; padding: 0; font-size: 8pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 7pt; }
          th, td { border: 1px solid #000 !important; padding: 2px !important; }
          .print-landscape { width: 100%; }
        }
      `}</style>

      <div className="p-2 print:p-1 print-landscape">
        {/* Top actions */}
        <div className="no-print flex items-center justify-between mb-3 gap-2">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>

          <div className="flex gap-2">
            <button
              onClick={() => downloadCsv(rows)}
              className="px-4 py-2 border rounded hover:bg-gray-50"
              disabled={!rows?.length}
              title={!rows?.length ? "No data" : "Export CSV"}
            >
              ⬇️ Export CSV
            </button>

            <button
              onClick={() => window.print()}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
            >
              🖨️ Print
            </button>
          </div>
        </div>

        {/* Loading/Error */}
        {loading ? (
          <div className="no-print p-4 border rounded bg-gray-50 text-sm">Loading print data...</div>
        ) : err ? (
          <div className="no-print p-4 border rounded bg-red-50 text-sm text-red-700">{err}</div>
        ) : null}

        {/* Header */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">CORE CONSTRUCTION</div>
              <div className="text-xs">{title}</div>
              <div className="text-[11px]">
                Mode: <b>{vehMode === "H" ? "Hire" : "Own"}</b> &nbsp;|&nbsp; Vehicle Type:{" "}
                <b>{vehicleType}</b> &nbsp;|&nbsp; Month: <b>{month}</b> &nbsp;|&nbsp; Year:{" "}
                <b>{year}</b>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 font-bold text-center" style={{ minWidth: 40 }}>
                  S.No
                </th>

                {columns.map((col) => (
                  <th
                    key={col}
                    className="border border-black px-1 py-1 font-bold text-center"
                    style={{ minWidth: col.length > 18 ? 120 : 80 }}
                    title={col}
                  >
                    {COMMON_LABELS[col] || col}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {!displayRows.length ? (
                <tr>
                  <td colSpan={1 + columns.length} className="border border-black px-2 py-3 text-center">
                    No data to print.
                  </td>
                </tr>
              ) : (
                displayRows.map((r) => (
                  <tr key={r.__sno} className="bg-white">
                    <td className="border border-black px-1 py-1 text-center">{r.__sno}</td>

                    {columns.map((col) => {
                      const val = r?.[col];

                      // Try to format numbers nicely for branch counts
                      const isCountish =
                        col !== "VehicleTypes" && col !== "VehicleType" && col !== "location";

                      const shown =
                        val === null || val === undefined || val === ""
                          ? ""
                          : isCountish && typeof val !== "object"
                          ? fmtInt(val)
                          : String(val);

                      return (
                        <td
                          key={`${r.__sno}-${col}`}
                          className="border border-black px-1 py-1"
                          style={{
                            textAlign:
                              col === "VehicleTypes" || col === "VehicleType" ? "left" : "center",
                          }}
                        >
                          {shown}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-2 text-xs border-2 border-black p-1">
          <p>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</p>
        </div>

        {/* Print Button */}
        <div className="mt-4 text-center no-print">
          <button
            onClick={() => window.print()}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow-lg transition-colors duration-200"
          >
            🖨️ Print Report
          </button>
        </div>
      </div>
    </div>
  );
}
